import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Lightbulb, MessageCircle, Wrench, Check, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import PortalBottomNav from "../components/PortalBottomNav";
import PortalHeader from "../components/PortalHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "idea", label: "An idea", icon: Lightbulb, hint: "Something Savi should try" },
  { id: "feedback", label: "Feedback", icon: MessageCircle, hint: "What's working, what's not" },
  { id: "fix", label: "Needs fixing", icon: Wrench, hint: "Something broken or missing" },
];

export default function FeedbackPage() {
  const [searchParams] = useSearchParams();
  const fromApp = searchParams.get("from") === "app";
  const [category, setCategory] = useState("idea");
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 3) {
      setError("Tell us a little more — even one sentence helps.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback/submit`, {
        name: name.trim() || null,
        category,
        message: message.trim(),
        website: website || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong — please try again, or just tell Steve in person.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`min-h-screen bg-[#FBF7F1] ${fromApp ? "pb-24 savi-bg-pattern" : ""}`}>
      <Helmet>
        <title>Ideas & Feedback — Savi Coliving</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header — app-style strip inside the portal, public logo nav otherwise */}
      {fromApp ? (
        <PortalHeader title="Got an idea?" testid="feedback-header" />
      ) : (
        <nav className="px-4 sm:px-6 py-4">
          <Link to="/" className="inline-flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_184a0a73-7ed5-43f8-9a23-0cd69b83d3bb/artifacts/rcargp5m_no%20bkg.png"
              alt="Savi"
              className="h-7 w-auto"
            />
            <span className="text-base tracking-wider text-[#C89B7B]">
              <span style={{ fontFamily: "Cinzel, serif" }}>SAVI</span>{" "}
              <span style={{ fontFamily: "'Cormorant Garamond', serif" }}>Coliving</span>
            </span>
          </Link>
        </nav>
      )}

      <main className="px-4 sm:px-6 pb-16 pt-6 sm:pt-10">
        <div className="max-w-xl mx-auto">
          {submitted ? (
            <div className="text-center py-16" data-testid="feedback-success">
              <div className="w-14 h-14 rounded-full bg-[#8C9A7C]/15 flex items-center justify-center mx-auto mb-6">
                <Check className="w-7 h-7 text-[#8C9A7C]" />
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl text-[#2E2A26] mb-3">
                Thank you{name.trim() ? `, ${name.trim().split(" ")[0]}` : ""}.
              </h1>
              <p className="text-[#2E2A26]/60 text-base leading-relaxed max-w-md mx-auto mb-8">
                Steve reads every single one. The best ideas at Savi have come from guests — yours might be next.
              </p>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setMessage("");
                }}
                className="text-[#C85A38] text-sm font-medium hover:underline"
                data-testid="feedback-submit-another"
              >
                Share another thought →
              </button>
            </div>
          ) : (
            <>
              <p className="font-label text-[#C85A38] text-xs tracking-[0.3em] uppercase mb-3">
                30 seconds · no login
              </p>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#2E2A26] mb-3 leading-tight">
                Got an idea for Savi?
              </h1>
              <p className="text-[#2E2A26]/60 text-base leading-relaxed mb-8">
                Big or small, serious or silly — if it would make life here better, we want to hear it.
                Name optional, honesty encouraged.
              </p>

              <form onSubmit={handleSubmit} data-testid="feedback-form">
                {/* Category pills */}
                <div className="grid grid-cols-3 gap-2 mb-6">
                  {CATEGORIES.map(({ id, label, icon: Icon, hint }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setCategory(id)}
                      className={`rounded-xl border p-3 sm:p-4 text-left transition-all ${
                        category === id
                          ? "border-[#C85A38] bg-[#C85A38]/5"
                          : "border-[#2E2A26]/10 bg-white hover:border-[#2E2A26]/25"
                      }`}
                      data-testid={`feedback-category-${id}`}
                    >
                      <Icon className={`w-5 h-5 mb-2 ${category === id ? "text-[#C85A38]" : "text-[#2E2A26]/40"}`} />
                      <p className="text-[#2E2A26] text-sm font-medium leading-tight">{label}</p>
                      <p className="text-[#2E2A26]/40 text-[11px] leading-tight mt-0.5 hidden sm:block">{hint}</p>
                    </button>
                  ))}
                </div>

                {/* Message */}
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  maxLength={3000}
                  placeholder={
                    category === "idea"
                      ? "e.g. A Sunday paella night, a book swap shelf, a cold plunge..."
                      : category === "feedback"
                      ? "e.g. The morning coworking energy is great, but evenings could use..."
                      : "e.g. The shower in room 3 drips, the terrace light is out..."
                  }
                  className="w-full rounded-xl border border-[#2E2A26]/10 bg-white p-4 text-[#2E2A26] text-base placeholder:text-[#2E2A26]/30 focus:outline-none focus:border-[#C85A38] transition-colors mb-4"
                  data-testid="feedback-message-input"
                />

                {/* Optional name */}
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={100}
                  placeholder="Your name (optional — anonymous is fine)"
                  className="w-full rounded-xl border border-[#2E2A26]/10 bg-white px-4 py-3 text-[#2E2A26] text-sm placeholder:text-[#2E2A26]/30 focus:outline-none focus:border-[#C85A38] transition-colors mb-2"
                  data-testid="feedback-name-input"
                />

                {/* Honeypot — hidden from humans */}
                <input
                  type="text"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  className="absolute opacity-0 h-0 w-0 pointer-events-none"
                  aria-hidden="true"
                />

                {error && (
                  <p className="text-[#C85A38] text-sm mb-2" data-testid="feedback-error">{error}</p>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#C85A38] hover:bg-[#B34E2E] text-white rounded-full py-6 text-base font-medium mt-2"
                  data-testid="feedback-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...
                    </>
                  ) : (
                    "Send it to Steve"
                  )}
                </Button>

                <p className="text-[#2E2A26]/40 text-xs text-center mt-4">
                  Goes straight to Steve — read within the day.
                </p>
              </form>
            </>
          )}
        </div>
      </main>

      <footer className="pb-10 text-center">
        <p className="font-serif italic text-[#2E2A26]/40 text-sm">A clear mind, in good company.</p>
      </footer>

      {fromApp && <PortalBottomNav />}
    </div>
  );
}
